import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';

const VITE_LEGACY_MODULE_SPECIFIER_RE = /Failed to resolve module specifier/i;

/**
 * Drop noisy false positives from @vitejs/plugin-legacy's modern-browser probe:
 *   import("_").catch(() => 1)
 * The bare specifier is meant to fail; Sentry's onunhandledrejection hook can
 * still capture it before the catch runs.
 */
function shouldDropSentryEvent(event: ErrorEvent): boolean {
  const message = event.exception?.values?.[0]?.value ?? event.message ?? '';
  const type = event.exception?.values?.[0]?.type ?? '';
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;

  if (type !== 'TypeError' || !VITE_LEGACY_MODULE_SPECIFIER_RE.test(message)) {
    return false;
  }

  // When mechanism metadata is present, only drop unhandled rejections from the probe.
  if (mechanismType && mechanismType !== 'onunhandledrejection') {
    return false;
  }

  return true;
}

function initSentry() {
  let sampleRate = 1.0;
  if (import.meta.env.PROD) {
    sampleRate = 0.1;
  }
  Sentry.init({
    dsn: 'https://f9fcafaaf56a44887389656e55cce60a@o4509731008479232.ingest.us.sentry.io/4510936167677952',
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: sampleRate,
    enabled: import.meta.env.PROD,
    beforeSend(event) {
      return shouldDropSentryEvent(event) ? null : event;
    },
  });
}

function setUserContext(userId: string) {
  Sentry.setUser({ id: userId });
}

export { initSentry, setUserContext, shouldDropSentryEvent };
